import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoGrid from "@/components/feature/VideoGrid";
import Pagination from "@/components/feature/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { paginate, searchVideos } from "@/utils/video";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const page = Number(searchParams.get("page")) || 1;
  const [input, setInput] = useState(keyword);

  useDocumentMeta(
    keyword ? `搜索「${keyword}」 - 污视频` : "搜索 - 污视频",
    keyword
      ? `污视频搜索结果：与「${keyword}」相关的视频资源，包含电影、电视剧、综艺等。`
      : "在污视频搜索你感兴趣的影视综资源。",
  );

  const result = useMemo(() => {
    if (!keyword) {
      return { pageItems: [], total: 0, totalPages: 1, page: 1 };
    }
    return paginate(searchVideos(keyword), page);
  }, [keyword, page]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <Layout>
      <div className="w-full px-4 pb-6 pt-6 md:px-8 md:pt-8">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
        />
        <div className="mt-5">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `「${keyword}」的搜索结果` : "搜索视频资源"}
          </h1>
          <p className="mt-1 text-sm text-foreground-500">
            {keyword
              ? `共找到 ${result.total} 条相关资源，结果按标题、标签与主演匹配`
              : "输入关键词，快速定位你想看的影视综内容"}
          </p>
        </div>

        <form
          onSubmit={submitSearch}
          className="mt-5 flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 sm:max-w-lg"
        >
          <i className="ri-search-line text-base text-foreground-400"></i>
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索视频标题 / 标签 / 主演"
            className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        {keyword ? (
          <>
            <VideoGrid
              videos={result.pageItems}
              emptyText={`没有找到与「${keyword}」相关的视频`}
            />
            <Pagination
              page={result.page}
              totalPages={result.totalPages}
              onChange={(nextPage) => {
                setSearchParams(
                  nextPage === 1
                    ? { q: keyword }
                    : { q: keyword, page: String(nextPage) },
                );
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">
              还没有输入搜索关键词
            </p>
            <p className="mt-1 text-sm text-foreground-400">
              试着搜索「悬疑」「古装」或某部作品的名字
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}